import { apiUrl, sensorsUrl } from "../api/endpoints";
import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/BuildCircleSharp';
import { STATIC_ICONS, BUCKET_DEVICES, defaultIcon } from '../Constants';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkAddedOutlinedIcon from '@mui/icons-material/BookmarkAddedOutlined';
import './details.css';
import './DashboardDetails.css';
import '../Components/modal.css';
import './TemplateDetails.css';
import { useAuth } from '../hooks/AuthProvider';
import axios from 'axios';
import CollapseContext from "../hooks/CollapseContext";
import useWindowDimensions from "../hooks/useWindowDimensions";
import Tooltip from '@mui/material/Tooltip';
import WebDashboard from './WebDashboard';
import Spinner from '../Components/Spinner';
import { DownloadDashboardReport } from "../Pages/DownloadDashboardReport";
import TuneIcon from '@mui/icons-material/Tune';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import TranferIcon from '@mui/icons-material/SyncAltOutlined';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { textFieldSlotProps } from './../Style';

import { LanguageContext } from '../context/LanguageContext';
import dashTranslations from '../languages/dashboard.json';
import deviceTranslations from '../languages/device.json';
import { getErrorMessage } from '../utils/errorUtils';
import { normalizeWebDashboardTabs } from '../utils/webDashboardLayout';
import { normalizeOrganizationListRow } from '../utils/listRowIds';
import DashboardTabsHeader from '../Components/DashboardTabsHeader';
import {
    aggregatePeriodColumnSeries,
    getPeriodColumnQueryRange,
    isNotFoundApiError,
    PERIOD_COLUMN_WIDGET_TYPE,
    normalizePeriodColumnPeriod,
    normalizePeriodColumnStat,
    resolvePeriodColumnWidgetBinding,
} from '../utils/periodColumnWidget';
import { buildChartSamplingPayload, getChartSamplingGroupKey } from '../utils/chartSampling';

import Alert from '../Components/Alert';
import { DownloadReprtIcon } from '../svg/UtilsIcons';
import { OrganizationIcon } from '../svg/DeviceIcons';
import { TemplateIcon } from '../svg/TemplateIcons';
import Select from 'react-select';

const initialDashboardState = () => ({
    id: 0,
    name: '',
    webdashboard: null,
    mobdashboard: null,
    icon: null,
    organization_id: 0,
    organization: {
        id: 0,
        name:'',
        devices: []
    },
    loading: true
});
const DASHBOARD_RANGE_SECONDS = [600, 3600, 21600, 86400, 604800, 2592000, 7776000, 15552000, 31104000];
const PERIOD_COLUMN_INFLUX_REFRESH_MS = 30000;
const normalizeExampleNameValue = (value) => String(value ?? '').trim().toLowerCase();
const sortDashboardExamples = (examples) =>
    [...(Array.isArray(examples) ? examples : [])].sort((left, right) =>
        String(left?.example_name || '').localeCompare(String(right?.example_name || ''), undefined, {
            sensitivity: 'base',
        })
    );

const checkTabPermission = (userRole, allowedRoles) => {
    const role = userRole?.toLowerCase();

    if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) {
        return false;
    }
    return allowedRoles.map(r => r.toLowerCase()).includes(role);
};

const addReferencedDeviceId = (deviceIds, value) => {
    const parsedValue = Number(value);
    if (Number.isInteger(parsedValue) && parsedValue > 0) {
        deviceIds.add(parsedValue);
    }
};

const collectReferencedDeviceIds = (widgets) => {
    const deviceIds = new Set();

    (Array.isArray(widgets) ? widgets : []).forEach((widget) => {
        if (!widget || typeof widget !== 'object') return;

        addReferencedDeviceId(deviceIds, widget?.device_id ?? widget?.deviceId);

        const curves = Array.isArray(widget?.extra_props?.curves) ? widget.extra_props.curves : [];
        curves.forEach((curve) => {
            addReferencedDeviceId(deviceIds, curve?.deviceId ?? curve?.device_id);
        });

        const widgetDatastreams = Array.isArray(widget?.extra_props?.datastreams)
            ? widget.extra_props.datastreams
            : [];
        widgetDatastreams.forEach((stream) => {
            addReferencedDeviceId(deviceIds, stream?.deviceId ?? stream?.device_id);
        });

        const widgetStreams = widget?.extra_props?.streams && typeof widget.extra_props.streams === 'object'
            ? Object.values(widget.extra_props.streams)
            : [];
        widgetStreams.forEach((stream) => {
            addReferencedDeviceId(deviceIds, stream?.deviceId ?? stream?.device_id);
        });

        const metrics = Array.isArray(widget?.extra_props?.metrics) ? widget.extra_props.metrics : [];
        metrics.forEach((metric) => {
            addReferencedDeviceId(deviceIds, metric?.deviceId ?? metric?.device_id);
        });
    });

    return deviceIds;
};


export const RenameDevice = ({ state, setState, refresh }) => {
    const { permissions } = useAuth();
    const { lang } = useContext(LanguageContext);

    const canRenameDevices = permissions['rename_devices'];

    const isRtl = ['ar', 'ur'].includes(lang);
    const t = deviceTranslations[lang].renameDevice;
    const [newName, setNewName] = useState('');
    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right'
    });
    const showAlert = (type, message) => {
        setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
    };

    const renameDevice = async () => {
        if (!newName || newName.trim() === '') {
            setAlertState({ open: true, type: 'error', message: t.renameError });
            return;
        }

        try {
            const res = await axios.post(
                apiUrl(`device/update/${state.id}`),
                { name: newName },
                { headers: { "Content-Type": "application/json" } }
            );

            showAlert('success', res.data.message || t.renameSuccess);

            refresh();
            setTimeout(() => {
                setState({ id: 0, open: false });
            }, 2000);

        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showAlert('error', errorMessage);
        }
    };

    if (!canRenameDevices) {
        return (
            <div id='modal'>
                <div id='popup' className='dd-modal-popup'>
                    <div className='modal-header'>
                        <h2>Permission Denied</h2>
                    </div>
                    <div className='modal-content'>
                        <p>You do not have permission to rename devices.</p>
                    </div>
                    <div className='modal-footer dd-modal-footer dd-modal-footer--header'>
                        <button className='btn btn-cancel'
                            onClick={() => setState({ open: false, id: 0 })}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id='modal'>
            <Alert
                open={alertState.open}
                close={() => setAlertState({ ...alertState, open: false })}
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />
            <div id='popup' className='dd-modal-popup'>
                <div className='modal-header'>
                    <h2>{t.title}</h2>
                </div>
                <div className='modal-content'>
                    <div className='form-item'>
                        <label className='form-lbl dd-form-lbl dd-form-lbl--mt'>
                            {t.label}
                        </label>

                        <div className='dd-name-wrap'>
                            <input
                                className='form-input dd-rename-input dd-input-counter'
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                maxLength={50}
                                onMouseEnter={() => {
                                    const helper = document.querySelector('.form-name-helper');
                                    if (helper) helper.style.opacity = 1;
                                }}
                                onMouseLeave={() => {
                                    const helper = document.querySelector('.form-name-helper');
                                    if (helper) helper.style.opacity = 0;
                                }}
                            />
                            <span className='form-helper dd-form-counter'>
                                {newName.length} / 50
                            </span>
                        </div>

                        <span
                            className='form-name-helper'
                            onMouseEnter={() => {
                                const helper = document.querySelector('.form-name-helper');
                                if (helper) helper.style.opacity = 1;
                            }}
                            onMouseLeave={() => {
                                const helper = document.querySelector('.form-name-helper');
                                if (helper) helper.style.opacity = 0;
                            }}
                        >
                            {t.helper}
                        </span>
                    </div>
                </div>
                <div className='modal-footer dd-modal-footer dd-modal-footer--header'>
                    <button className='btn btn-cancel dd-btn-cancel-invert'
                        onClick={() => setState({ open: false, id: 0 })}>
                        {t.buttonCancel}</button>
                    <button className='btn btn-apply dd-btn-apply'
                        onClick={renameDevice}>{t.button}</button>
                </div>
            </div>
        </div>
    );
};

export const TransferDashboard = ({ state, setState, refresh }) => {
    const { permissions } = useAuth();
    const canTransferDashboards = permissions['edit_dashboard'];

    const { lang } = useContext(LanguageContext);
    const isRtl = ['ar', 'ur'].includes(lang);
    const t = dashTranslations?.[lang]?.transferDashboard || {};

    const [organizations, setOrganizations] = useState([]);
    const [newOrganization, setNewOrganization] = useState({});
    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right'
    });

    const showAlert = useCallback((type, message) => {
        setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
    }, [isRtl]);

    const closeModal = useCallback(() => {
        setOrganizations([]);
        setNewOrganization({});
        setState({ open: false, id: 0 });
    }, [setState]);

    useEffect(() => {
        if (!state?.open || !canTransferDashboards) return;

        const fetchOrganizations = async () => {
            try {
                const organizationResponse = await axios.get(apiUrl(`organization/list`));

                if (organizationResponse.data?.status === 'OK' && Array.isArray(organizationResponse.data.data)) {
                    setOrganizations(organizationResponse.data.data.map(normalizeOrganizationListRow));
                } else {
                    setOrganizations([]);
                    showAlert('error', t.invalidOrganizationData || 'Received invalid organization data from the server.');
                }
            } catch (err) {
                setOrganizations([]);
                showAlert('error', getErrorMessage(err));
            }
        };
        fetchOrganizations();
    }, [state?.open, canTransferDashboards, showAlert, t.invalidOrganizationData]);

    const organizationOptions = useMemo(
        () =>
            (organizations || []).map((org) => ({
                value: org.id,
                label: org.name,
                ...org,
            })),
        [organizations]
    );

    const selectedOrganizationOption = useMemo(
        () =>
            organizationOptions.find(
                (option) => Number(option.value) === Number(newOrganization?.id)
            ) || null,
        [newOrganization?.id, organizationOptions]
    );

    const transferDashboard = async () => {
        if (!newOrganization?.id) {
            showAlert('error', t.transferOrgError || 'Please select a destination organization.');
            return;
        }

        try {
            const res = await axios.post(
                apiUrl(`dashboard/organization-transfer/${state.id}`),
                { organization_id: newOrganization.id },
                { headers: { 'Content-Type': 'application/json' } }
            );

            showAlert('success', res.data?.message || t.transferSuccess || 'Dashboard transferred successfully.');
            refresh?.();
            setTimeout(() => closeModal(), 1200);
        } catch (err) {
            showAlert('error', getErrorMessage(err));
        }
    };

    if (!state?.open) return null;

    if (!canTransferDashboards) {
        return (
            <div id='modal' dir={isRtl ? 'rtl' : 'ltr'}>
                <div id='popup' className='dashboard-modal'>
                    <div className='dashboard-header'>
                        <div className='dashboard-header-text'>
                            <h2>{t.permissionDeniedTitle || 'Permission Denied'}</h2>
                            <p>{t.permissionDeniedMessage || 'You do not have permission to transfer dashboards.'}</p>
                        </div>
                    </div>
                    <div className='modal-divider' />
                    <div className='modal-content dashboard-content' />
                    <div className='modal-footer dashboard-footer'>
                        <div />
                        <div className='modal-footer-right-buttons'>
                            <button className='btn btn-cancel'
                                onClick={closeModal}>
                                {t.buttonClose || 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id='modal' dir={isRtl ? 'rtl' : 'ltr'}>
            <Alert
                open={alertState.open}
                close={() => setAlertState({ ...alertState, open: false })}
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />
            <div id='popup' className='dashboard-modal'>
                <div className='dashboard-header'>
                    <div className='dashboard-header-text'>
                        <h2>{t.title || 'Transfer Dashboard'}</h2>
                        <p>{t.description || 'Transfer this dashboard to another organization.'}</p>
                    </div>
                </div>
                <div className='modal-divider' />
                <div className='modal-content dashboard-content'>
                    <div className='form-section'>
                        <div className='form-item'>
                            <label className='form-lbl'>
                                {t.inputOrg || 'Destination organization'}
                            </label>
                            <Select
                                inputId={'dashboard-organization-transfer'}
                                options={organizationOptions}
                                value={selectedOrganizationOption}
                                onChange={(selectedOption) => setNewOrganization(selectedOption || null)}
                                placeholder={t.inputOrg || 'Destination organization'}
                                classNamePrefix="react-select"
                                isClearable
                                isRtl={isRtl}
                                isDisabled={organizationOptions.length === 0}
                            />
                        </div>
                    </div>
                </div>
                <div className='modal-footer dashboard-footer'>
                    <div />
                    <div className='modal-footer-right-buttons'>
                        <button className='btn btn-cancel'
                            onClick={closeModal}>
                            {t.buttonCancel || 'Cancel'}
                        </button>
                        <button className='btn btn-apply'
                            onClick={transferDashboard}>
                            {t.buttonTransfer || 'Transfer'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};



export const DashboardDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { lang } = useContext(LanguageContext);
    const trans = dashTranslations[lang];
    const t = trans.dashboardDetails;
    const isRtl = ['ar', 'ur'].includes(lang);

    const [dashboard, setDashboard] = useState(initialDashboardState());

    const { user, permissions } = useAuth();
    const userRole = user.role?.toLowerCase();

    const dims = useWindowDimensions();
    const [currentDashboard, setCurrentDashboard] = useState(0);
    const [currentRange, setCurrentRange] = useState(0);
    const [customFrom, setCustomFrom] = useState(null);
    const [customTo, setCustomTo] = useState(null);
    const [report, setReport] = useState({
        open: false,
        id: 0,
        devices: undefined
    });
    const [transfer, setTransfer] = useState({
        open: false,
        id: 0
    });
    const [reloadTick, setReloadTick] = useState(0);
    const [loadingReport, setLoadingReport] = useState(false);
    const [isDashboardExpanded, setIsDashboardExpanded] = useState(false);
    const [dashboardViewportHeight, setDashboardViewportHeight] = useState(null);
    const previousSidenavOpenRef = useRef(false);
    const wasDashboardExpandedRef = useRef(false);
    const dashboardRef = useRef(dashboard);
    const dashboardBodyRef = useRef(null);
    const dashboardViewportRef = useRef(null);
    const currentDashboardRef = useRef(currentDashboard);
    const currentRangeRef = useRef(currentRange);
    const customFromRef = useRef(customFrom);
    const customToRef = useRef(customTo);
    const streamSourceRef = useRef(null);
    const periodColumnRefreshTimeoutRef = useRef(null);
    const periodColumnLastRefreshAtRef = useRef(Date.now());
    const historyRequestVersionRef = useRef(0);

    const open = useContext(CollapseContext);
    const maxWidth = '100%';
    const hasWebDashboard = Boolean(dashboard?.webdashboard?.length);
    const fallbackDashboardHeight = isDashboardExpanded
        ? Math.max(320, dims.height - 180)
        : Math.max(280, dims.height - 300);
    const maxHeight = `${dashboardViewportHeight || fallbackDashboardHeight}px`;
    const ranges = t.ranges;
    const currentRangeKey = ranges?.[currentRange]?.key || '';
    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right'
    });
    const showAlert = useCallback((type, message) => { 
        setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
    }, [isRtl]);
    const [dashboardExamples, setDashboardExamples] = useState([]);
    const [loadingDashboardExamples, setLoadingDashboardExamples] = useState(false);
    const [hasLoadedDashboardExamples, setHasLoadedDashboardExamples] = useState(false);
    const [markAsExampleDialog, setMarkAsExampleDialog] = useState({
        open: false,
        exampleName: '',
        error: '',
        submitting: false,
    });
    const setSidenavOpen = useCallback((nextOpen) => {
        window.dispatchEvent(
            new CustomEvent('sama:sidenav:set-open', {
                detail: { open: Boolean(nextOpen) },
            }),
        );
    }, []);

    useEffect(() => {
        dashboardRef.current = dashboard;
    }, [dashboard]);

    useEffect(() => {
        currentDashboardRef.current = currentDashboard;
    }, [currentDashboard]);

    useEffect(() => {
        currentRangeRef.current = currentRange;
    }, [currentRange]);

    useEffect(() => {
        customFromRef.current = customFrom;
    }, [customFrom]);

    useEffect(() => {
        customToRef.current = customTo;
    }, [customTo]);

    const loadDashboardExamples = useCallback(async () => {
        if (loadingDashboardExamples) {
            return dashboardExamples;
        }

        setLoadingDashboardExamples(true);
        try {
            const res = await axios.get(apiUrl(`dashboard/examples/list`));
            if (res.data?.status !== 'OK' || !Array.isArray(res.data.data)) {
                throw new Error('Invalid dashboard examples response.');
            }

            const sortedExamples = sortDashboardExamples(res.data.data);
            setDashboardExamples(sortedExamples);
            setHasLoadedDashboardExamples(true);
            return sortedExamples;
        } catch (err) {
            console.error('[DashboardDetails] Failed to load dashboard examples:', err);
            showAlert('error', t.markAsExampleLoadError || 'Failed to load dashboard examples.');
            return [];
        } finally {
            setLoadingDashboardExamples(false);
        }
    }, [dashboardExamples, loadingDashboardExamples, showAlert, t.markAsExampleLoadError]);

    const normalizedExistingExampleNames = useMemo(
        () =>
            new Set(
                dashboardExamples
                    .map((example) => normalizeExampleNameValue(example?.example_name))
                    .filter(Boolean)
            ),
        [dashboardExamples]
    );

    const normalizedDialogExampleName = useMemo(
        () => normalizeExampleNameValue(markAsExampleDialog.exampleName),
        [markAsExampleDialog.exampleName]
    );

    const isExampleNameDuplicate = useMemo(
        () =>
            Boolean(
                normalizedDialogExampleName &&
                    hasLoadedDashboardExamples &&
                    normalizedExistingExampleNames.has(normalizedDialogExampleName)
            ),
        [hasLoadedDashboardExamples, normalizedDialogExampleName, normalizedExistingExampleNames]
    );

    const canManageDashboardExamples = permissions['add_new_dashoard'];

    const openMarkAsExampleDialog = useCallback(async () => {
        if (!canManageDashboardExamples) {
            showAlert('error', t.markAsExamplePermissionError || 'You do not have permission to perform this action.');
            return;
        }

        if (!dashboard?.id) {
            showAlert('warning', t.markAsExampleDashboardMissing || 'Dashboard details are not available yet.');
            return;
        }

        setMarkAsExampleDialog({
            open: true,
            exampleName: String(dashboard?.name || '').trim(),
            error: '',
            submitting: false,
        });

        if (!hasLoadedDashboardExamples && !loadingDashboardExamples) {
            await loadDashboardExamples();
        }
    }, [
        canManageDashboardExamples,
        dashboard,
        hasLoadedDashboardExamples,
        loadDashboardExamples,
        loadingDashboardExamples,
        showAlert,
        t.markAsExampleDashboardMissing,
        t.markAsExamplePermissionError,
    ]);

    const closeMarkAsExampleDialog = useCallback(() => {
        setMarkAsExampleDialog((prev) =>
            prev.submitting
                ? prev
                : {
                      open: false,
                      exampleName: '',
                      error: '',
                      submitting: false,
                  }
        );
    }, []);

    const handleMarkAsExampleNameChange = useCallback((event) => {
        const nextValue = event.target.value;
        setMarkAsExampleDialog((prev) => ({
            ...prev,
            exampleName: nextValue,
            error: '',
        }));
    }, []);

    const submitMarkAsExample = useCallback(async () => {
        if (!canManageDashboardExamples) {
            showAlert('error', t.markAsExamplePermissionError || 'You do not have permission to perform this action.');
            return;
        }

        if (!dashboard?.id) {
            showAlert('warning', t.markAsExampleDashboardMissing || 'Dashboard details are not available yet.');
            return;
        }

        const trimmedExampleName = String(markAsExampleDialog.exampleName || '').trim();
        if (!trimmedExampleName) {
            setMarkAsExampleDialog((prev) => ({
                ...prev,
                error: t.markAsExampleNameRequired || 'Example name is required.',
            }));
            return;
        }

        if (hasLoadedDashboardExamples && normalizedExistingExampleNames.has(normalizeExampleNameValue(trimmedExampleName))) {
            setMarkAsExampleDialog((prev) => ({
                ...prev,
                error: t.markAsExampleUnique || 'Example name must be unique.',
            }));
            return;
        }

        setMarkAsExampleDialog((prev) => ({
            ...prev,
            submitting: true,
            error: '',
        }));

        try {
            const res = await axios.post(
                apiUrl(`dashboard/examples/from-dashboard/${dashboard.id}`),
                { example_name: trimmedExampleName },
                { headers: { 'Content-Type': 'application/json' } }
            );

            if (res.status !== 201 || res.data?.status !== 'OK') {
                throw new Error('Received an unexpected response from the server.');
            }

            setDashboardExamples((prev) => {
                const next = prev.filter(
                    (example) =>
                        normalizeExampleNameValue(example?.example_name) !==
                        normalizeExampleNameValue(trimmedExampleName)
                );
                next.push(res.data.data);
                return sortDashboardExamples(next);
            });
            setHasLoadedDashboardExamples(true);
            setMarkAsExampleDialog({
                open: false,
                exampleName: '',
                error: '',
                submitting: false,
            });
            showAlert('success', res.data.message || (t.markAsExampleSuccess || 'Dashboard example created successfully.'));
        } catch (err) {
            const errorMessage = getErrorMessage(err);
            setMarkAsExampleDialog((prev) => ({
                ...prev,
                submitting: false,
                error: errorMessage,
            }));
            showAlert('error', errorMessage);
        }
    }, [
        canManageDashboardExamples,
        dashboard?.id,
        hasLoadedDashboardExamples,
        markAsExampleDialog.exampleName,
        normalizedExistingExampleNames,
        showAlert,
        t.markAsExampleDashboardMissing,
        t.markAsExampleNameRequired,
        t.markAsExamplePermissionError,
        t.markAsExampleSuccess,
        t.markAsExampleUnique,
    ]);

    const isMarkAsExampleSubmitDisabled =
        markAsExampleDialog.submitting ||
        !String(markAsExampleDialog.exampleName || '').trim() ||
        isExampleNameDuplicate;

    const updateDashboardViewportHeight = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const bodyElement = dashboardBodyRef.current;
        const viewportElement = dashboardViewportRef.current;

        if (!bodyElement || !viewportElement) {
            setDashboardViewportHeight(null);
            return;
        }

        const bodyRect = bodyElement.getBoundingClientRect();
        const viewportRect = viewportElement.getBoundingClientRect();
        const dashboardTopSpacing = 8;
        const nextHeight = Math.floor(bodyRect.bottom - viewportRect.top - dashboardTopSpacing);

        if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
            setDashboardViewportHeight(null);
            return;
        }

        setDashboardViewportHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    }, []);

    useEffect(() => () => {
        if (periodColumnRefreshTimeoutRef.current) {
            clearTimeout(periodColumnRefreshTimeoutRef.current);
            periodColumnRefreshTimeoutRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!hasWebDashboard) {
            setDashboardViewportHeight(null);
            return undefined;
        }

        if (typeof window === 'undefined') {
            return undefined;
        }

        let rafId = 0;
        const scheduleViewportMeasure = () => {
            if (rafId) {
                window.cancelAnimationFrame(rafId);
            }

            rafId = window.requestAnimationFrame(() => {
                updateDashboardViewportHeight();
            });
        };

        scheduleViewportMeasure();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', scheduleViewportMeasure);

            return () => {
                if (rafId) {
                    window.cancelAnimationFrame(rafId);
                }
                window.removeEventListener('resize', scheduleViewportMeasure);
            };
        }

        const resizeObserver = new ResizeObserver(() => {
            scheduleViewportMeasure();
        });

        [dashboardBodyRef.current, dashboardViewportRef.current]
            .filter(Boolean)
            .forEach((element) => resizeObserver.observe(element));

        window.addEventListener('resize', scheduleViewportMeasure);

        return () => {
            if (rafId) {
                window.cancelAnimationFrame(rafId);
            }
            resizeObserver.disconnect();
            window.removeEventListener('resize', scheduleViewportMeasure);
        };
    }, [
        hasWebDashboard,
        isDashboardExpanded,
        updateDashboardViewportHeight,
    ]);

    useEffect(() => {
        if (isDashboardExpanded && !wasDashboardExpandedRef.current) {
            const sidenavElement = document.querySelector('[data-sidenav-open]');
            const attrValue = sidenavElement?.getAttribute('data-sidenav-open');
            previousSidenavOpenRef.current = attrValue ? attrValue === 'true' : Boolean(open);
            setSidenavOpen(false);
        } else if (!isDashboardExpanded && wasDashboardExpandedRef.current) {
            setSidenavOpen(previousSidenavOpenRef.current);
        }

        wasDashboardExpandedRef.current = isDashboardExpanded;
    }, [isDashboardExpanded, open, setSidenavOpen]);

    useEffect(() => {
        if (typeof document === 'undefined') return undefined;

        document.body.classList.toggle('dashboard-view-fullscreen', isDashboardExpanded);
        window.requestAnimationFrame?.(() => window.dispatchEvent(new Event('resize')));

        return () => {
            document.body.classList.remove('dashboard-view-fullscreen');
        };
    }, [isDashboardExpanded]);

    useEffect(() => {
        if (!isDashboardExpanded) return;

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsDashboardExpanded(false);
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isDashboardExpanded]);

    useEffect(() => {
        return () => {
            if (wasDashboardExpandedRef.current) {
                setSidenavOpen(previousSidenavOpenRef.current);
            }
        };
    }, [setSidenavOpen]);

    const fetchAllChartAndTableDataForCurrentTab = useCallback(async (refreshMode = 'all') => {
        const dashboardState = dashboardRef.current;
        const webdashboardTabs = dashboardState?.webdashboard;
        const devicesList = dashboardState?.organization?.devices;
        const selectedDashboardIndex = currentDashboardRef.current;
        const selectedRangeIndex = currentRangeRef.current;
        const selectedCurrentRangeKey = ranges?.[selectedRangeIndex]?.key || '';
        const selectedCustomFrom = customFromRef.current;
        const selectedCustomTo = customToRef.current;
        const requestVersion = historyRequestVersionRef.current + 1;

        historyRequestVersionRef.current = requestVersion;

        if (!webdashboardTabs || webdashboardTabs.length === 0 || !devicesList) {
            return;
        }

        const currentTab = webdashboardTabs[selectedDashboardIndex];
        if (!currentTab || !Array.isArray(currentTab.widgets)) {
            return;
        }

        const shouldRefreshCharts = refreshMode !== 'period-only';
        const chartAndTableWidgets = shouldRefreshCharts ? currentTab.widgets.filter(
            widget => widget.type === 'chart' || widget.type === 'table'
        ) : [];
        const periodColumnWidgets = currentTab.widgets.filter(
            widget => widget.type === PERIOD_COLUMN_WIDGET_TYPE
        );

        if (chartAndTableWidgets.length === 0 && periodColumnWidgets.length === 0) {
            return;
        }

        let tstart = null;
        let tstop = null;
        let rangeSeconds = 0;
        let rangeWindowSec = 0;

        if (chartAndTableWidgets.length > 0) {
            const now = dayjs();

            if (selectedCurrentRangeKey === 'Custom' && selectedCustomFrom && selectedCustomTo) {
                const fromEpoch = selectedCustomFrom.unix();
                const toEpoch = selectedCustomTo.unix();

                if (fromEpoch >= toEpoch) {
                    showAlert('error', t.customRangeInvalid || "Invalid custom range: 'From' date must be before 'To' date.");
                    return;
                }
                tstart = fromEpoch;
                tstop = toEpoch;
                rangeSeconds = tstop - tstart;
            } else {
                const epoch = now.unix();
                tstart = epoch - DASHBOARD_RANGE_SECONDS[selectedRangeIndex];
                tstop = epoch;
                rangeSeconds = DASHBOARD_RANGE_SECONDS[selectedRangeIndex];
            }

            if (!tstart || !tstop) {
                console.warn("fetchAllChartAndTableDataForCurrentTab: tstart or tstop is not defined. Aborting.");
                return;
            }

            rangeWindowSec = selectedCurrentRangeKey === 'Custom' && selectedCustomFrom && selectedCustomTo
                ? Math.max(0, tstop - tstart)
                : (DASHBOARD_RANGE_SECONDS[selectedRangeIndex] || 0);
        }

        const fieldQueryGroups = new Map();

        chartAndTableWidgets.forEach(widget => {
            const samplingKey = getChartSamplingGroupKey({
                props: widget?.extra_props || {},
                legacyMode: 'dashboard',
            });

            if (!fieldQueryGroups.has(samplingKey)) {
                fieldQueryGroups.set(samplingKey, {
                    key: samplingKey,
                    sampling: buildChartSamplingPayload({
                        props: widget?.extra_props || {},
                        legacyMode: 'dashboard',
                    }),
                    fields: new Map(),
                });
            }

            const group = fieldQueryGroups.get(samplingKey);

            (widget?.extra_props?.curves || []).forEach(curve => {
                const pin = String(curve?.pin || '').trim();
                if (!pin || pin === 'Pin') return;

                const device = devicesList?.find(d => Number(d?.id) === Number(curve?.deviceId));
                if (!device?.template?.tid || !device?.did) {
                    return;
                }

                const aggregation = String(curve?.aggregation || 'mean').trim() || 'mean';
                const queryKey = [device.did, device.template.tid, pin, aggregation].join('|');
                group.fields.set(queryKey, {
                    field: pin,
                    fn: aggregation,
                    device: device.did,
                    measurement: device.template.tid
                });
            });
        });

        let multiSeriesResultByTwin = new Map();
        let periodWidgetResultMap = new Map();

        try {
            if (fieldQueryGroups.size > 0) {
                const groupedResults = await Promise.all(
                    Array.from(fieldQueryGroups.values()).map(async (group) => {
                        const fieldsToQuery = Array.from(group.fields.values());

                        if (fieldsToQuery.length === 0) {
                            return [group.key, null];
                        }

                        const res = await axios.post(
                            apiUrl(`data/fquery_multi`),
                            {
                                bucket: BUCKET_DEVICES,
                                fields: fieldsToQuery,
                                tstart: tstart,
                                tstop: tstop,
                                sampling: group.sampling,
                            },
                            { headers: { "Content-Type": "application/json" } }
                        );

                        return [group.key, res.data.data];
                    })
                );

                multiSeriesResultByTwin = new Map(groupedResults);
            }

            if (periodColumnWidgets.length > 0) {
                const periodWidgetResults = await Promise.all(periodColumnWidgets.map(async (widget) => {
                    const { device, pin } = resolvePeriodColumnWidgetBinding({
                        widget,
                        devices: devicesList,
                    });
                    const normalizedPeriod = normalizePeriodColumnPeriod(widget?.extra_props?.period);
                    const normalizedStat = normalizePeriodColumnStat(widget?.extra_props?.stat);
                    const {
                        tstart: periodTstart,
                        tstop: periodTstop,
                        rangeWindowSec: periodRangeWindowSec,
                    } = getPeriodColumnQueryRange(normalizedPeriod);
                    const queryPeriod = normalizedPeriod === 'week' ? 'day' : normalizedPeriod;

                    if (!device?.template?.tid || !device?.did || !pin) {
                        return {
                            widgetId: widget.id,
                            period: normalizedPeriod,
                            stat: normalizedStat,
                            tstart: periodTstart,
                            tstop: periodTstop,
                            rangeWindowSec: periodRangeWindowSec,
                            xData: [],
                            yData: [],
                        };
                    }

                    const fetchRawAggregatedSeries = async () => {
                        const rawRes = await axios.post(
                            apiUrl(`data/fquery`),
                            {
                                bucket: BUCKET_DEVICES,
                                measurement: device.template.tid,
                                device: device.did,
                                fields: [{ field: pin, fn: 'raw' }],
                                tstart: periodTstart,
                                tstop: periodTstop,
                                twin: '1',
                            },
                            { headers: { "Content-Type": "application/json" } }
                        );

                        const rawTimes = Array.isArray(rawRes?.data?.data?.t) ? rawRes.data.data.t : [];
                        const rawValues = Array.isArray(rawRes?.data?.data?.[pin]) ? rawRes.data.data[pin] : [];

                        return aggregatePeriodColumnSeries({
                            timestamps: rawTimes,
                            values: rawValues,
                            period: normalizedPeriod,
                            stat: normalizedStat,
                            nowInput: new Date(periodTstop * 1000),
                        });
                    };

                    let res;
                    try {
                        res = await axios.post(
                            apiUrl(`data/pquery`),
                            {
                                bucket: BUCKET_DEVICES,
                                measurement: device.template.tid,
                                device: device.did,
                                field: pin,
                                period: queryPeriod,
                                stat: normalizedStat,
                                tstart: periodTstart,
                                tstop: periodTstop,
                            },
                            { headers: { "Content-Type": "application/json" } }
                        );
                    } catch (error) {
                        if (!isNotFoundApiError(error)) {
                            throw error;
                        }

                        const aggregated = await fetchRawAggregatedSeries();

                        return {
                            widgetId: widget.id,
                            period: normalizedPeriod,
                            stat: normalizedStat,
                            tstart: periodTstart,
                            tstop: periodTstop,
                            rangeWindowSec: periodRangeWindowSec,
                            xData: aggregated.xData,
                            yData: aggregated.yData,
                        };
                    }

                    const responseTimes = Array.isArray(res?.data?.data?.t) ? res.data.data.t : [];
                    const responseValues = Array.isArray(res?.data?.data?.values) ? res.data.data.values : [];
                    const aggregated = aggregatePeriodColumnSeries({
                        timestamps: responseTimes,
                        values: responseValues,
                        period: normalizedPeriod,
                        stat: normalizedStat,
                        nowInput: new Date(periodTstop * 1000),
                    });

                    return {
                        widgetId: widget.id,
                        period: normalizedPeriod,
                        stat: normalizedStat,
                        tstart: periodTstart,
                        tstop: periodTstop,
                        rangeWindowSec: periodRangeWindowSec,
                        xData: aggregated.xData,
                        yData: aggregated.yData,
                    };
                }));

                periodWidgetResultMap = new Map(
                    periodWidgetResults.map((item) => [item.widgetId, item])
                );
            }

            if (requestVersion !== historyRequestVersionRef.current) {
                return;
            }

            setDashboard(prev => ({
                ...prev,
                webdashboard: prev.webdashboard.map((dash, idx) =>
                    idx === selectedDashboardIndex
                        ? {
                            ...dash,
                            widgets: dash.widgets.map(w => {
                                if (w.type === 'chart' || w.type === 'table') {
                                    const samplingKey = getChartSamplingGroupKey({
                                        props: w?.extra_props || {},
                                        legacyMode: 'dashboard',
                                    });
                                    const multiSeriesResult = multiSeriesResultByTwin.get(samplingKey);

                                    if (!multiSeriesResult) {
                                        return w;
                                    }

                                    const xData = Array.isArray(multiSeriesResult?.t) ? multiSeriesResult.t : [];
                                    const updatedCurves = (w?.extra_props?.curves || []).map(c => {
                                        const device = prev.organization?.devices?.find(d => d.id === c.deviceId);
                                        const deviceDid = device?.did ? String(device.did).trim() : '';
                                        const pinData = deviceDid && c?.pin
                                            ? multiSeriesResult?.[deviceDid]?.[c.pin]
                                            : null;
                                        return Array.isArray(pinData) ? { ...c, yData: pinData } : c;
                                    });

                                    return {
                                        ...w,
                                        extra_props: {
                                            ...w.extra_props,
                                            xData: xData,
                                            min: dayjs.unix(tstart).toISOString(),
                                            max: dayjs.unix(tstop).toISOString(),
                                            isLiveRange: selectedRangeIndex === 0,
                                            rangeWindowSec: rangeWindowSec,
                                            curves: updatedCurves
                                        }
                                    };
                                }

                                if (w.type === PERIOD_COLUMN_WIDGET_TYPE) {
                                    const periodData = periodWidgetResultMap.get(w.id);
                                    if (!periodData) {
                                        return {
                                            ...w,
                                            extra_props: {
                                                ...w.extra_props,
                                                isLiveRange: true,
                                            }
                                        };
                                    }

                                    return {
                                        ...w,
                                        extra_props: {
                                            ...w.extra_props,
                                            xData: periodData.xData,
                                            yData: periodData.yData,
                                            period: periodData.period,
                                            stat: periodData.stat,
                                            min: dayjs.unix(periodData.tstart).toISOString(),
                                            max: dayjs.unix(periodData.tstop).toISOString(),
                                            isLiveRange: true,
                                            rangeWindowSec: periodData.rangeWindowSec,
                                        }
                                    };
                                }

                                return w;
                            })
                        }
                        : dash
                )
            }));

        } catch (err) {
            if (requestVersion !== historyRequestVersionRef.current) {
                return;
            }
            const errorMessage = getErrorMessage(err);
            showAlert('error', `Could not load chart data: ${errorMessage}`);
        }
    }, [ranges, showAlert]);


    useEffect(() => {
        const fetchDashboardDetails = async () => {
            setDashboard(prev => ({ ...prev, loading: true }));

            try {
                const res = await axios.get(apiUrl(`dashboard/get/${id}`), {
                    params: { client_platform: 'web' }
                });

                if (res.data && typeof res.data.data === 'object' && res.data.data !== null) {

                    const dashboardData = res.data.data;

                    if (dashboardData.webdashboard && Array.isArray(dashboardData.webdashboard)) {
                        dashboardData.webdashboard = normalizeWebDashboardTabs(dashboardData.webdashboard).filter(wd =>
                            checkTabPermission(userRole, wd.access_role || wd.access_roles)
                        );
                    }

                    setDashboard(prev => ({
                        ...prev,
                        ...dashboardData,
                        loading: false
                    }));

                } else {
                    throw new Error("Received an invalid data format from the server.");
                }

            } catch (err) {
                const errorMessage = getErrorMessage(err);
                setAlertState({
                    open: true,
                    message: errorMessage,
                    type: 'error',
                    position: isRtl ? 'left' : 'right'
                });

                setDashboard(prev => ({
                    ...initialDashboardState(),
                    loading: false
                }));

                if (err.response && err.response.status === 404) {
                    setTimeout(() => navigate('/dashboards'), 3000);
                }
            }
        };

        fetchDashboardDetails();
    }, [id, navigate, userRole, isRtl, reloadTick]);

    const moveBorder = useCallback((className, originalIndex) => {
        const border = document.querySelector('.' + className + '-tab-border');
        const targetTab = document.querySelector(
            `.${className}-tab[data-original-index="${originalIndex}"]`
        );

        if (border && targetTab) {
            const dashboardWidth = document.defaultView.getComputedStyle(targetTab).width;
            border.style.width = dashboardWidth;
            border.style.left = targetTab.offsetLeft + 'px';
            targetTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
    }, []);

    useEffect(() => {
        if (dashboard && dashboard.webdashboard && dashboard.webdashboard.length > 0) {
            const timeout =
                dashboard.webdashboard && dashboard.webdashboard.length > 0 ? 0 : 500;
            setTimeout(() => {
                requestAnimationFrame(() => moveBorder('dashboard', currentDashboard));
            }, timeout);
        }
    }, [currentDashboard, dashboard, moveBorder]);

    useEffect(() => {
        if (dashboard.loading || !dashboard.webdashboard || dashboard.webdashboard.length === 0) {
            return;
        }
        if (currentRangeKey === 'Custom' && (!customFrom || !customTo)) {
            return;
        }
        fetchAllChartAndTableDataForCurrentTab();
    }, [
        dashboard.loading,
        dashboard.webdashboard?.length,
        currentDashboard,
        currentRange,
        currentRangeKey,
        customFrom,
        customTo,
        fetchAllChartAndTableDataForCurrentTab
    ]);


    useEffect(() => {
        const fetchTerminalData = async () => {
            const currentTab = dashboard.webdashboard?.[currentDashboard];
            if (!currentTab || !dashboard.organization?.devices) {
                return;
            }

            const terminalPromises = [];
            const promiseMetadata = [];

            currentTab.widgets.forEach(widget => {
                if (widget.type === 'terminal') {
                    const device = dashboard.organization.devices.find(d => d.id === widget.device_id);
                    const datastream = device?.template?.datastreams.find(ds => ds.id === widget.data_id);

                    if (device && datastream) {
                        const body = {
                            bucket: 'Sensors',
                            measurement: device.template.tid,
                            device: device.did,
                            field: datastream.pin
                        };

                        const promise = axios.post(
                            apiUrl(`data/tquery`),
                            body,
                            { headers: { "Content-Type": "application/json" } }
                        );

                        terminalPromises.push(promise);

                        promiseMetadata.push({ widgetId: widget.id, deviceId: device.id });
                    }
                }
            });

            if (terminalPromises.length === 0) {
                return;
            }

            try {

                const responses = await Promise.all(terminalPromises);

                setDashboard(prev => {
                    const resultsMap = new Map();
                    responses.forEach((res, index) => {
                        const meta = promiseMetadata[index];
                        const key = `${meta.widgetId}-${meta.deviceId}`;
                        resultsMap.set(key, res.data.data);
                    });

                    return {
                        ...prev,
                        webdashboard: prev.webdashboard.map((dash, idx) =>
                            idx === currentDashboard
                                ? {
                                    ...dash,
                                    widgets: dash.widgets.map(w => {
                                        const resultKey = `${w.id}-${w.device_id}`;
                                        const result = resultsMap.get(resultKey);

                                        if (w.type === 'terminal' && result) {
                                            return {
                                                ...w,
                                                extra_props: {
                                                    ...w.extra_props,
                                                    value: w.extra_props.newLine
                                                        ? '\r\n' + result?.text?.join('\r\n')
                                                        : result?.text?.join('')
                                                }
                                            };
                                        }
                                        return w;
                                    })
                                }
                                : dash
                        )
                    };
                });

            } catch (err) {
                console.error("Error fetching terminal data:", err);
                const errorMessage = getErrorMessage(err);
                showAlert('error', `Could not load terminal data: ${errorMessage}`);
            }
        };

        fetchTerminalData();

    }, [dashboard.organization?.devices?.length, currentDashboard, dashboard.webdashboard?.length]); 

    const normalizeMultiStreamPayload = useCallback((payload) => {
        const updates = [];

        const pushUpdate = (candidate) => {
            if (!candidate || typeof candidate !== 'object') return;
            const pins = candidate?.pins && typeof candidate.pins === 'object'
                ? candidate.pins
                : null;

            let derivedPins = pins;
            if (!derivedPins) {
                const { did: _did, id: _id, device: _device, ...restPins } = candidate;
                derivedPins = Object.keys(restPins || {}).length > 0 ? restPins : null;
            }

            if (!derivedPins) return;

            const didRaw = candidate?.did ?? candidate?.device?.did ?? null;
            const idRaw = candidate?.id ?? candidate?.device?.id ?? null;

            const did = didRaw == null ? null : String(didRaw).trim();
            const parsedId = Number(idRaw);
            const id = Number.isFinite(parsedId) ? parsedId : null;

            if (!did && id == null) return;
            updates.push({ did: did || null, id, pins: derivedPins });
        };

        if (Array.isArray(payload)) {
            payload.forEach((item) => pushUpdate(item));
        } else if (payload && typeof payload === 'object') {
            pushUpdate(payload);
        }

        if (updates.length === 0) return [];

        const mergedByKey = new Map();
        updates.forEach((update) => {
            const key = update.did ? `did:${update.did}` : `id:${update.id}`;
            const existing = mergedByKey.get(key);
            mergedByKey.set(key, {
                did: update.did,
                id: update.id,
                pins: {
                    ...(existing?.pins || {}),
                    ...(update?.pins || {})
                }
            });
        });

        return Array.from(mergedByKey.values());
    }, []);

    const applyLiveStreamPayload = useCallback((streamUpdates) => {
        if (!Array.isArray(streamUpdates) || streamUpdates.length === 0) return;

        setDashboard((prev) => {
            const tabIndex = currentDashboardRef.current;
            const isLiveRange = currentRangeRef.current === 0;
            const tabs = Array.isArray(prev?.webdashboard) ? prev.webdashboard : [];
            const targetTab = tabs[tabIndex];
            const devices = Array.isArray(prev?.organization?.devices) ? prev.organization.devices : [];

            if (!targetTab || !Array.isArray(targetTab.widgets) || devices.length === 0) {
                return prev;
            }

            const pinsByDid = new Map();
            const pinsById = new Map();
            streamUpdates.forEach((update) => {
                if (!update?.pins || typeof update.pins !== 'object') return;
                if (update?.did) {
                    const key = String(update.did).trim();
                    pinsByDid.set(key, {
                        ...(pinsByDid.get(key) || {}),
                        ...update.pins
                    });
                }
                if (update?.id != null) {
                    const key = Number(update.id);
                    pinsById.set(key, {
                        ...(pinsById.get(key) || {}),
                        ...update.pins
                    });
                }
            });

            const getPinsForDevice = (device) => {
                if (!device) return null;
                const didKey = String(device?.did || '').trim();
                if (didKey && pinsByDid.has(didKey)) return pinsByDid.get(didKey);
                const idKey = Number(device?.id);
                if (Number.isFinite(idKey) && pinsById.has(idKey)) return pinsById.get(idKey);
                return null;
            };

            let devicesChanged = false;
            const nextDevices = devices.map((device) => {
                const pins = getPinsForDevice(device);
                if (!pins) return device;

                const dsList = Array.isArray(device?.template?.datastreams)
                    ? device.template.datastreams
                    : [];
                if (dsList.length === 0) return device;

                let datastreamChanged = false;
                const nextDatastreams = dsList.map((ds) => {
                    const pin = ds?.pin;
                    if (!pin || !Object.prototype.hasOwnProperty.call(pins, pin)) return ds;
                    const nextValue = pins[pin];
                    if (ds?.value === nextValue) return ds;
                    datastreamChanged = true;
                    return { ...ds, value: nextValue };
                });

                if (!datastreamChanged) return device;
                devicesChanged = true;
                return {
                    ...device,
                    template: {
                        ...device.template,
                        datastreams: nextDatastreams
                    }
                };
            });

            const effectiveDevices = devicesChanged ? nextDevices : devices;
            const devicesById = new Map(
                effectiveDevices.map((d) => [Number(d?.id), d]).filter(([id]) => Number.isFinite(id))
            );

            let changed = false;
            const nowMs = Date.now();

            const updatedWidgets = targetTab.widgets.map((widget) => {
                if (!widget || typeof widget !== 'object') return widget;

                if (widget.type === 'chart' || widget.type === 'table') {
                    const curves = Array.isArray(widget?.extra_props?.curves) ? widget.extra_props.curves : [];
                    if (!isLiveRange || curves.length === 0) return widget;

                    const hasMatchingCurve = curves.some((curve) => {
                        const curveDevice = devicesById.get(Number(curve?.deviceId));
                        const curvePins = getPinsForDevice(curveDevice);
                        return Boolean(curvePins && curve?.pin && Object.prototype.hasOwnProperty.call(curvePins, curve.pin));
                    });
                    if (!hasMatchingCurve) return widget;

                    const nextX = Array.isArray(widget?.extra_props?.xData) ? [...widget.extra_props.xData] : [];
                    const lastMs = nextX.length ? Date.parse(nextX[nextX.length - 1]) : null;
                    const pointMs = Number.isFinite(lastMs) && nowMs <= lastMs ? lastMs + 1 : nowMs;
                    const nextIso = new Date(pointMs).toISOString();
                    nextX.push(nextIso);

                    const rangeWindowSec = Number(widget?.extra_props?.rangeWindowSec) || 0;
                    let trimmedCount = 0;
                    if (rangeWindowSec > 0) {
                        const cutoffMs = pointMs - rangeWindowSec * 1000;
                        while (nextX.length > 0) {
                            const t = Date.parse(nextX[0]);
                            if (Number.isFinite(t) && t < cutoffMs) {
                                nextX.shift();
                                trimmedCount += 1;
                            } else {
                                break;
                            }
                        }
                    }

                    const nextCurves = curves.map((curve) => {
                        const prevY = Array.isArray(curve?.yData) ? [...curve.yData] : [];
                        const curveDevice = devicesById.get(Number(curve?.deviceId));
                        const curvePins = getPinsForDevice(curveDevice);
                        const streamRaw = curvePins ? curvePins[curve?.pin] : undefined;
                        const raw = streamRaw !== undefined ? streamRaw : (prevY.length ? prevY[prevY.length - 1] : null);
                        const numeric = Number(raw);
                        const nextVal = Number.isFinite(numeric) ? numeric : (raw ?? null);
                        prevY.push(nextVal);
                        if (trimmedCount > 0) {
                            prevY.splice(0, trimmedCount);
                        }
                        return { ...curve, yData: prevY };
                    });

                    changed = true;
                    return {
                        ...widget,
                        extra_props: {
                            ...widget.extra_props,
                            xData: nextX,
                            curves: nextCurves,
                            min: rangeWindowSec > 0
                                ? new Date(pointMs - rangeWindowSec * 1000).toISOString()
                                : widget?.extra_props?.min,
                            max: new Date(pointMs).toISOString(),
                        }
                    };
                }

                if (widget.type === 'time-range') {
                    const currentStreams = widget?.extra_props?.streams || {};
                    const nextStreams = { ...currentStreams };
                    let streamChanged = false;

                    ['hours', 'minutes'].forEach((streamKey) => {
                        const configuredStream = currentStreams?.[streamKey];
                        if (!configuredStream?.dataId) return;

                        const streamDeviceId = Number(configuredStream?.deviceId || widget?.device_id || 0);
                        const streamDevice = devicesById.get(streamDeviceId);
                        if (!streamDevice?.did) return;

                        const streamDs = streamDevice?.template?.datastreams?.find(
                            (datastream) => Number(datastream?.id) === Number(configuredStream?.dataId)
                        );
                        if (!streamDs?.pin) return;

                        const streamPins = getPinsForDevice(streamDevice);
                        const nextValue = streamPins ? streamPins[streamDs.pin] : undefined;
                        if (nextValue === undefined || configuredStream?.value === nextValue) return;

                        streamChanged = true;
                        nextStreams[streamKey] = {
                            ...configuredStream,
                            value: nextValue
                        };
                    });

                    if (!streamChanged) return widget;
                    changed = true;
                    return {
                        ...widget,
                        extra_props: {
                            ...widget.extra_props,
                            streams: nextStreams
                        }
                    };
                }

                const widgetDevice = devicesById.get(Number(widget?.device_id));
                const widgetDatastream = widgetDevice?.template?.datastreams?.find(
                    (datastream) => Number(datastream?.id) === Number(widget?.data_id)
                );

                if (widget.type === 'fan') {
                    const activeDatastream = (widget?.extra_props?.datastreams || []).find((item) => item?.isActive);
                    const activePin = activeDatastream?.pin;
                    const widgetPins = getPinsForDevice(widgetDevice);
                    const nextFanValue = widgetPins && activePin ? widgetPins[activePin] : undefined;
                    if (nextFanValue === undefined || widget?.extra_props?.value === nextFanValue) {
                        return widget;
                    }
                    changed = true;
                    return {
                        ...widget,
                        extra_props: {
                            ...widget.extra_props,
                            value: nextFanValue
                        }
                    };
                }

                if (!widgetDevice?.did || !widgetDatastream?.pin) {
                    return widget;
                }

                const widgetPins = getPinsForDevice(widgetDevice);
                const nextValue = widgetPins ? widgetPins[widgetDatastream.pin] : undefined;
                if (nextValue === undefined) return widget;

                if (widget.type === 'terminal') {
                    const incomingText = String(nextValue ?? '');
                    if (!incomingText) return widget;
                    const previousLiveValue = widget?.extra_props?.__lastLiveValue == null
                        ? null
                        : String(widget.extra_props.__lastLiveValue);
                    if (previousLiveValue === incomingText) return widget;

                    const previousText = widget?.extra_props?.value == null
                        ? ''
                        : String(widget.extra_props.value);
                    const separator = widget?.extra_props?.newLine ? '\r\n' : '';
                    const nextText = previousText ? `${previousText}${separator}${incomingText}` : incomingText;

                    changed = true;
                    return {
                        ...widget,
                        extra_props: {
                            ...widget.extra_props,
                            value: nextText,
                            __lastLiveValue: incomingText
                        }
                    };
                }

                if (widget?.extra_props?.value === nextValue) {
                    return widget;
                }

                changed = true;
                return {
                    ...widget,
                    extra_props: {
                        ...widget.extra_props,
                        value: nextValue
                    }
                };
            });

            if (!changed && !devicesChanged) return prev;

            return {
                ...prev,
                organization: devicesChanged
                    ? {
                        ...prev.organization,
                        devices: nextDevices
                    }
                    : prev.organization,
                webdashboard: tabs.map((tab, index) =>
                    index === tabIndex ? { ...tab, widgets: updatedWidgets } : tab
                )
            };
        });
    }, []);

    const schedulePeriodColumnRefresh = useCallback((streamUpdates) => {
        const currentDashboardState = dashboardRef.current;
        const currentTabIndex = currentDashboardRef.current;
        const currentTab = currentDashboardState?.webdashboard?.[currentTabIndex];
        if (!currentTab?.widgets?.length) {
            return;
        }

        const touchedPinsByDid = new Map();
        (Array.isArray(streamUpdates) ? streamUpdates : []).forEach((update) => {
            if (!update?.pins || typeof update.pins !== 'object' || !update?.did) {
                return;
            }

            touchedPinsByDid.set(String(update.did).trim(), update.pins);
        });

        const hasMatchingPeriodWidget = currentTab.widgets.some((widget) => {
            if (widget?.type !== PERIOD_COLUMN_WIDGET_TYPE) {
                return false;
            }

            const { device, pin } = resolvePeriodColumnWidgetBinding({
                widget,
                devices: currentDashboardState?.organization?.devices || [],
            });

            const deviceDid = String(device?.did || '').trim();
            const touchedPins = touchedPinsByDid.get(deviceDid);
            return Boolean(touchedPins && pin && Object.prototype.hasOwnProperty.call(touchedPins, pin));
        });

        if (!hasMatchingPeriodWidget) {
            return;
        }

        // Period columns aggregate historical Influx data. Refreshing them for every
        // live sensor message creates a continuous Influx query loop.
        if (periodColumnRefreshTimeoutRef.current) {
            return;
        }

        const scheduledDashboardIndex = currentDashboardRef.current;
        const elapsedMs = Date.now() - periodColumnLastRefreshAtRef.current;
        const refreshDelayMs = Math.max(750, PERIOD_COLUMN_INFLUX_REFRESH_MS - elapsedMs);

        periodColumnRefreshTimeoutRef.current = setTimeout(() => {
            periodColumnRefreshTimeoutRef.current = null;
            if (currentDashboardRef.current !== scheduledDashboardIndex) {
                return;
            }
            periodColumnLastRefreshAtRef.current = Date.now();
            fetchAllChartAndTableDataForCurrentTab('period-only');
        }, refreshDelayMs);
    }, [fetchAllChartAndTableDataForCurrentTab]);

    const streamDeviceIds = useMemo(() => {
        const currentTab = dashboard?.webdashboard?.[currentDashboard];
        const devices = Array.isArray(dashboard?.organization?.devices)
            ? dashboard.organization.devices
            : [];

        if (!currentTab?.widgets?.length || devices.length === 0) {
            return '';
        }

        const referencedDeviceIds = collectReferencedDeviceIds(currentTab.widgets);
        if (referencedDeviceIds.size === 0) {
            return '';
        }

        return devices
            .filter((device) => referencedDeviceIds.has(Number(device?.id)))
            .map((device) => String(device?.did || '').trim())
            .filter(Boolean)
            .join(',');
    }, [currentDashboard, dashboard?.organization?.devices, dashboard?.webdashboard]);

    useEffect(() => {
        if (!streamDeviceIds) {
            if (streamSourceRef.current) {
                streamSourceRef.current.close();
                streamSourceRef.current = null;
            }
            return;
        }

        if (streamSourceRef.current) {
            streamSourceRef.current.close();
            streamSourceRef.current = null;
        }

        const source = new EventSource(
            sensorsUrl(`multi-stream?devices_ids=${encodeURIComponent(streamDeviceIds)}`)
        );
        streamSourceRef.current = source;

        source.onmessage = (event) => {
            try {
                const parsed = JSON.parse(event.data);
                const streamUpdates = normalizeMultiStreamPayload(parsed);
                applyLiveStreamPayload(streamUpdates);
                schedulePeriodColumnRefresh(streamUpdates);
            } catch (err) {
                console.error('Failed to parse multi-stream payload:', err);
            }
        };

        source.onerror = (error) => {
            console.error('EventSource multi-stream failed:', error);
        };

        return () => {
            if (streamSourceRef.current === source) {
                streamSourceRef.current.close();
                streamSourceRef.current = null;
            } else {
                source.close();
            }
        };
    }, [applyLiveStreamPayload, normalizeMultiStreamPayload, schedulePeriodColumnRefresh, streamDeviceIds]);
    const canEditDashboards = permissions['edit_dashboard']; 
    const canTransferDashboards = permissions['edit_dashboard'];
    const canDownloadReports = permissions['download_dashboard_reports'];
    const canDownloadAiReports = permissions['download_ai_reports'];
    const canOpenReportDialog = true;
    const downloadReportActionLabel = canDownloadReports
        ? (t.downloadReport || 'Download Report')
        : canDownloadAiReports
            ? (t.downloadAiReport || 'Download AI Report')
            : (t.downloadReport || 'Download Report');
    const canShowActions =
        canEditDashboards ||
        canTransferDashboards ||
        canOpenReportDialog ||
        canManageDashboardExamples;

    const renderRangeControls = () => (
        <div className='details-time-range'>
            {ranges.map(({ key, label }, idx) => {
                const isActive = currentRange === idx;
                const content =
                    key === 'Custom' ? (
                        <TuneIcon className='dd-range-tune-icon' />
                    ) : (
                        label
                    );

                return (
                    <button
                        key={key}
                        type='button'
                        className={`dd-range-btn ${isActive ? 'active' : ''}`}
                        onClick={() => {
                            setCurrentRange(idx);
                            if (key !== 'Custom') {
                                setCustomFrom(null);
                                setCustomTo(null);
                            }
                        }}
                    >
                        {content}
                    </button>
                );
            })}

            {ranges[currentRange].key === 'Custom' && (
                <div className='dd-custom-range'>
                    {/*
                      Use a dedicated class/paper styling here so the custom range
                      matches the Device Details popover inputs instead of the
                      generic compact picker field.
                    */}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            value={customFrom}
                            onChange={(newValue) =>
                                setCustomFrom(newValue)
                            }
                            maxDateTime={customTo || dayjs()}
                            format="DD/MM/YYYY hh:mm A"
                            slotProps={{
                                ...(textFieldSlotProps || {}),
                                textField: {
                                    ...((textFieldSlotProps && textFieldSlotProps.textField) ? textFieldSlotProps.textField : {}),
                                    className: 'dd-custom-date-field',
                                    fullWidth: false,
                                    placeholder: t.dateTimePlaceholder || 'Select date & time',
                                },
                                popper: {
                                    ...((textFieldSlotProps && textFieldSlotProps.popper) ? textFieldSlotProps.popper : {}),
                                    className: 'dd-custom-date-popper',
                                    placement: isRtl ? 'bottom-start' : 'bottom-end',
                                },
                                desktopPaper: {
                                    className: 'dd-date-picker-paper',
                                },
                                dialog: {
                                    className: 'dd-date-picker-dialog',
                                },
                            }}
                        />

                        {isRtl ? (
                            <ArrowBackIosIcon className='dd-range-arrow' />
                        ) : (
                            <ArrowRightAltIcon className='dd-range-arrow' />
                        )}

                        <DateTimePicker
                            value={customTo}
                            onChange={setCustomTo}
                            minDateTime={customFrom}
                            maxDateTime={dayjs()}
                            format="DD/MM/YYYY hh:mm A"
                            slotProps={{
                                ...(textFieldSlotProps || {}),
                                textField: {
                                    ...((textFieldSlotProps && textFieldSlotProps.textField) ? textFieldSlotProps.textField : {}),
                                    className: 'dd-custom-date-field',
                                    fullWidth: false,
                                    placeholder: t.dateTimePlaceholder || 'Select date & time',
                                },
                                popper: {
                                    ...((textFieldSlotProps && textFieldSlotProps.popper) ? textFieldSlotProps.popper : {}),
                                    className: 'dd-custom-date-popper',
                                    placement: isRtl ? 'bottom-start' : 'bottom-end',
                                },
                                desktopPaper: {
                                    className: 'dd-date-picker-paper',
                                },
                                dialog: {
                                    className: 'dd-date-picker-dialog',
                                },
                            }}
                        />
                    </LocalizationProvider>
                </div>
            )}
        </div>
    );

    const renderExpandToggle = (extraClassName = '') => {
        if (!hasWebDashboard) return null;
        const className = `dd-expand-toggle${isDashboardExpanded ? ' active' : ''}${extraClassName ? ` ${extraClassName}` : ''}`;
        const expandLabel = isDashboardExpanded
            ? (t.collapseAction || 'Click to collapse')
            : (t.expandAction || 'Click to expand');
        return (
            <button
                type='button'
                className={className}
                onClick={() => setIsDashboardExpanded((prev) => !prev)}
                title={expandLabel}
                aria-label={expandLabel}
            >
                {isDashboardExpanded ? (
                    <CloseFullscreenIcon className='dd-expand-toggle-icon' />
                ) : (
                    <OpenInFullIcon className='dd-expand-toggle-icon' />
                )}
                <span className='dd-expand-toggle-label'>
                    {expandLabel}
                </span>
            </button>
        );
    };

    return (dashboard.loading ? <Spinner />
        : permissions['view_dashboards'] ? 
            dashboard ?
                <div
                    className={`details-container dd-dashboard-details-container${isDashboardExpanded ? ' dd-dashboard-expanded-container' : ''}`}
                >
                    <Alert
                        open={alertState.open}
                        close={() => setAlertState({ ...alertState, open: false })}
                        message={alertState.message}
                        type={alertState.type}
                        position={alertState.position}
                    />
                    {typeof document !== 'undefined' && markAsExampleDialog.open
                        ? createPortal(
                              <div
                                  id="modal"
                                  onMouseDown={() => {
                                      if (!markAsExampleDialog.submitting) {
                                          closeMarkAsExampleDialog();
                                      }
                                  }}
                              >
                                  <div
                                      id="popup"
                                      className="dashboard-modal td-example-modal"
                                      role="dialog"
                                      aria-modal="true"
                                      aria-labelledby="dashboard-example-modal-title"
                                      onMouseDown={(event) => event.stopPropagation()}
                                  >
                                      <div className="dashboard-header td-example-modal__header">
                                          <div className="dashboard-header-icon">
                                              <TemplateIcon style={{ color: "var(--details-icons-color, var(--sidenav-icons, #FFFFFF))" }} />
                                          </div>

                                          <div className="dashboard-header-text">
                                              <h2 id="dashboard-example-modal-title">
                                                  {t.markAsExample || 'Mark as Example'}
                                              </h2>
                                              <p>
                                                  {t.markAsExampleDescription ||
                                                      'Choose a unique example name and we will copy this dashboard into the examples library.'}
                                              </p>
                                          </div>

                                          <button
                                              type="button"
                                              className="td-example-modal__close"
                                              onClick={closeMarkAsExampleDialog}
                                              disabled={markAsExampleDialog.submitting}
                                              aria-label={t.close || 'Close'}
                                          >
                                              <CloseIcon fontSize="small" />
                                          </button>
                                      </div>

                                      <div className="modal-divider" />

                                      <div className="modal-content dashboard-content td-example-modal__content">
                                          <div className="form-item">
                                              <label
                                                  className="form-lbl"
                                                  htmlFor="dashboard-example-name"
                                                  style={{ color: 'var(--header-color)', marginLeft: 0, marginRight: 0 }}
                                              >
                                                  {t.exampleNameLabel || 'Example Name'}
                                              </label>
                                              <input
                                                  id="dashboard-example-name"
                                                  type="text"
                                                  className="add-column td-example-modal__input"
                                                  placeholder={t.exampleNamePlaceholder || 'Enter a unique example name'}
                                                  value={markAsExampleDialog.exampleName}
                                                  onChange={handleMarkAsExampleNameChange}
                                                  onKeyDown={(event) => {
                                                      if (event.key === 'Enter') {
                                                          event.preventDefault();
                                                          if (!isMarkAsExampleSubmitDisabled) {
                                                              submitMarkAsExample();
                                                          }
                                                      }
                                                  }}
                                                  autoFocus
                                                  maxLength={100}
                                                  disabled={markAsExampleDialog.submitting}
                                              />
                                              <div className="td-example-modal__helper">
                                                  {loadingDashboardExamples
                                                      ? (t.markAsExampleChecking || 'Checking existing examples...')
                                                      : (t.markAsExampleHelper || 'This name must be unique across all dashboard examples.')}
                                              </div>
                                              {isExampleNameDuplicate && !markAsExampleDialog.error ? (
                                                  <div className="td-example-modal__error">
                                                      {t.markAsExampleUnique || 'Example name must be unique.'}
                                                  </div>
                                              ) : null}
                                              {markAsExampleDialog.error ? (
                                                  <div className="td-example-modal__error">
                                                      {markAsExampleDialog.error}
                                                  </div>
                                              ) : null}
                                          </div>

                                          <div className="td-example-modal__copy-card">
                                              <div className="td-example-modal__copy-title">
                                                  {t.markAsExampleCopiedFields || 'Copied from this dashboard'}
                                              </div>
                                              <div className="td-example-modal__copy-list">
                                                  {[
                                                      t.exampleCopyName || 'Name',
                                                      t.exampleCopyDescription || 'Description',
                                                      t.exampleCopyWebDashboard || 'Web Dashboard',
                                                      t.exampleCopyMobileDashboard || 'Mobile Dashboard',
                                                      t.exampleCopyNullDevices || 'Device bindings cleared',
                                                      t.exampleCopyRoles || 'Tabs limited to Super / Owner',
                                                  ].map((label) => (
                                                      <span key={label} className="td-example-modal__copy-pill">
                                                          {label}
                                                      </span>
                                                  ))}
                                              </div>
                                          </div>
                                      </div>

                                      <div className="modal-divider" />

                                      <div className="modal-footer dashboard-footer">
                                          <button
                                              className="btn btn-cancel-details"
                                              onClick={closeMarkAsExampleDialog}
                                              disabled={markAsExampleDialog.submitting}
                                          >
                                              {t.cancel || 'Cancel'}
                                          </button>

                                          <div className="modal-footer-right-buttons">
                                              <button
                                                  className="btn btn-apply"
                                                  onClick={submitMarkAsExample}
                                                  disabled={isMarkAsExampleSubmitDisabled}
                                              >
                                                  {markAsExampleDialog.submitting
                                                      ? (t.markAsExampleSaving || 'Saving...')
                                                      : (t.markAsExampleSave || 'Save as Example')}
                                              </button>
                                          </div>
                                      </div>
                                  </div>
                              </div>,
                              document.body
                          )
                        : null}
                    {report.open && (
                        <DownloadDashboardReport
                            devices={dashboard.organization.devices}
                            organizationId={dashboard.organization_id}
                            dashboardId={dashboard.id}
                            dashboardName={dashboard.name}
                            dashboardType={dashboard.type}
                            dashboardWorkDaysHours={dashboard.work_days_hours}
                            dashboardTimezone={dashboard.organization?.timezone}
                            state={report}
                            setState={setReport}
                            setLoadingReport={setLoadingReport}
                            onAlert={showAlert}
                            refresh={() => navigate('/report')}
                        />
                    )}
                    {transfer.open && (
                        <TransferDashboard
                            state={transfer}
                            setState={setTransfer}
                            refresh={() => setReloadTick((prev) => prev + 1)}
                        />
                    )}
                    {loadingReport && (
                        <div className='dd-report-toast'>
                            <div className='dd-report-toast-header'>
                                <span className='dd-report-toast-title'>
                                    {t.generatingReport}
                                </span>
                                <span
                                    className='dd-report-toast-close'
                                    onClick={() => setLoadingReport(false)}
                                >✖</span>
                            </div>
                            <span className='dd-report-toast-sub'>
                                {t.reportReady}
                            </span>
                        </div>

                    )}
<div className="details-control-panel">
    <div className="details-control-panel-inside" />

    <div className="details-control-panel-glow" />

    <div className="details-control-panel-content">
        <div className="details-control-btns details-control-btns-column">
            <button
                type="button"
                className="details-control-btn details-control-btn-ghost"
                onClick={() => navigate('/dashboards')}
            >
                <small>{'\u2715'}</small>
            </button>
        </div>

    </div>
</div>


                    <div
                        className={`details-content dd-dashboard-details-content${isDashboardExpanded ? ' dd-dashboard-content-expanded' : ''}`}
                    >
<div className={`details-head dd-dashboard-details-head${isDashboardExpanded ? ' dd-dashboard-head-expanded' : ''}`}>
    <div className={`dd-dashboard-hero${isDashboardExpanded ? ' is-hidden' : ''}`}>
        <img
            alt='icon'
            src={`${STATIC_ICONS}${
                dashboard.icon?.length > 0 ? dashboard.icon : defaultIcon
            }`}
        />
    </div>

    <div className='details-remain-head dd-remain-head'>
        <div
            className={`details-remain-head-part dd-remain-head-part dd-remain-head-part--top${isDashboardExpanded ? ' dd-remain-head-part--top-expanded' : ''}`}
        >
            <div className='details-head-left'>
                <h1 className='dd-title'>
                    {dashboard.name}
                </h1>
                {isDashboardExpanded ? (
                    <div className='dd-head-inline-range'>
                        {renderRangeControls()}
                    </div>
                ) : null}
            </div>
        </div>

        <div className={`details-head-meta-row dd-dashboard-expandable-meta${isDashboardExpanded ? ' is-hidden' : ''}`}>
            {dashboard.organization?.name ? (
                <div
                    className={`details-meta-pill dd-meta-pill ${permissions['edit_organizations'] ? 'dd-clickable' : ''}`}
                    onClick={
                        permissions['edit_organizations']
                            ? () =>
                                  navigate(
                                      '/administration/organizations/details/' +
                                          dashboard.organization_id,
                                  )
                            : undefined
                    }
                >
                    <OrganizationIcon color={'var(--primary)'} />
                    <span>{dashboard.organization.name}</span>
                </div>
            ) : null}
        </div>

        <div
            className='details-remain-head-part dd-remain-head-part dd-remain-head-part--row'
        >
                    {canShowActions && (
                <div className='details-inline-actions'>
                    {canOpenReportDialog && (
                        <Tooltip title={downloadReportActionLabel}>
                            <button
                                type='button'
                                className='details-inline-action-btn'
                                onClick={() =>
                                    setReport({
                                        open: true,
                                        id: dashboard.id,
                                        devices: dashboard.organization.devices,
                                    })
                                }
                            >
                                <DownloadReprtIcon />
                            </button>
                        </Tooltip>
                    )}
                    {canManageDashboardExamples && (
                        <Tooltip title={t.markAsExample || 'Mark as Example'}>
                            <button
                                type='button'
                                className='details-inline-action-btn'
                                onClick={openMarkAsExampleDialog}
                            >
                                <BookmarkAddedOutlinedIcon fontSize="small" style={{ color: "var(--details-icons-color, var(--sidenav-icons, #FFFFFF))" }} />
                            </button>
                        </Tooltip>
                    )}
                    {canTransferDashboards && (
                        <Tooltip title={trans?.transferDashboard?.title || 'Transfer Dashboard'}>
                            <button
                                type='button'
                                className='details-inline-action-btn'
                                onClick={() =>
                                    setTransfer({
                                        open: true,
                                        id: dashboard.id
                                    })
                                }
                            >
                                <TranferIcon style={{ color: "var(--details-icons-color, var(--sidenav-icons, #FFFFFF))" }}/>
                            </button>
                        </Tooltip>
                    )}
                </div>
            )}

            <div className='dd-actions-right'>
                {!isDashboardExpanded ? renderRangeControls() : null}

                {canEditDashboards && (
                    <button
                        type='button'
                        className='details-edit-btn'
                        onClick={
                            dashboard && dashboard.id
                                ? () =>
                                      navigate(
                                          '/dashboards/edit-dashboard/' +
                                              dashboard.id +
                                              '?tab=1&mode=edit',
                                      )
                                : null
                        }
                    >
                        <EditIcon />
                        <span className='dd-edit-btn-text'>
                            {t.editDashboard}
                        </span>
                    </button>
                )}
                {!isDashboardExpanded ? renderExpandToggle() : null}
            </div>
        </div>
    </div>
</div>

                        {dashboard && dashboard.webdashboard && dashboard.webdashboard.length > 0 ?
                            <div
                                ref={dashboardBodyRef}
                                className={`details-body dd-details-body${isDashboardExpanded ? ' dd-details-body-expanded' : ''}`}
                            >
                                <DashboardTabsHeader
                                    className={`dashboard-tabs-shell${isDashboardExpanded ? ' dd-dashboard-tabs-expanded' : ''}`}
                                    tabsClassName="dashboard-tabs dd-dashboard-tabs"
                                    actions={isDashboardExpanded ? (
                                        <div className='dd-dashboard-tabs-actions'>
                                            {renderExpandToggle('dd-expand-toggle-tabs')}
                                        </div>
                                    ) : null}
                                >
                                        {dashboard.webdashboard.map((wd, idx) => (<div
                                            key={idx}
                                            className={`dashboard-tab dd-dashboard-tab ${currentDashboard === idx ? 'active' : ''}`}
                                            data-original-index={idx}
                                            title={wd.name}
                                            onClick={() => setCurrentDashboard(idx)}>
                                            <span>{wd.name}</span>
                                        </div>))}
                                        <div className='dashboard-tab-border'></div>
                                </DashboardTabsHeader>
                                <div ref={dashboardViewportRef} className='dd-dashboard-viewport'>
                                    <WebDashboard mode={'view'} devices={dashboard.organization.devices}
                                        datastreams={dashboard.datastreams} maxWidth={maxWidth}
                                        maxHeight={maxHeight}
                                        dashboard={dashboard.webdashboard[currentDashboard]}
                                        setDashboard={(webDashboard) => setDashboard(prev => ({
                                            ...prev,
                                            webdashboard: prev.webdashboard.map(
                                                (wd, ix) => ix === currentDashboard ? webDashboard : wd)
                                        }))} />
                                </div>
                            </div> :
                            <div className='details-body'>{t.noDataBody}</div>}
                    </div>
                </div> : <div className='dd-center-full'>{trans.noData}</div>
            : <div>{trans.noPermission}</div>
    );
};
